import { get, groupBy, isString } from 'lodash';
import { Integration, IntegrationAuthType, IntegrationPlatform } from './types';

/**
 * formatGroupedIntegrations
 * mobile apps can have multiple integrations of the same tyoe
 * this helper sets up integrations by type, returning an object with:
 * [platformType]: [array of integrations]
 */
export const formatGroupedIntegrations = (_key: string, integrations: Integration[]) =>
  integrations.reduce((obj, int) => {
    const formatted: { [key: string]: any } = { ...obj };
    const { platform } = int;
    if (!formatted[platform]) {
      formatted[platform] = [];
    }
    formatted[platform] = [...formatted[platform], int];
    return { ...formatted };
  }, {});

/**
 * groupIntegrations
 * takes in integrations and returns them grouped by:
 * mobileAppId or by unassigned if no mobile app is assigned.
 * Additinoally keys those grouped by mobileAppId by their platform.
 * @param { Array } integrations: List of integrations to group by.
 * @returns { Object } The grouped integrations
 */
export const groupIntegrations = (integrations: Integration[]) => {
  const groupedIntegrations = groupBy(integrations, 'mobileAppId');

  return Object.keys(groupedIntegrations).reduce(
    (obj, key) =>
      key === 'undefined' // no mobileAppId
        ? { ...obj, unassigned: groupedIntegrations[key] }
        : { ...obj, [key]: formatGroupedIntegrations(key, groupedIntegrations[key]) },
    {},
  );
};

/**
 * gets human readable error message,
 * should a human readable error message be returned in the response object
 * @param { Object } error: The error object.
 * @returns { String } The formatted error message.
 */
export const extractErrorMessage = (error: Record<string, any>) => {
  const FALLBACK_ERROR_MESSAGE = 'Something went wrong';

  /** If the backend throws any unexpected exception--even if the main request itself is successful--then
   * we do not want to the response data because it includes HTML of the error page for 500 errors.
   * We are opting to suppress it to the generic error message. */
  if (error?.response?.status === 500) return FALLBACK_ERROR_MESSAGE;

  /* Backend sometimes returns "response.data" as the error string itself and
   * sometimes returns an error object. We should account for both cases but
   * prioritize the one with an error object. */
  const responseData = get(error, 'response.data.message') || get(error, 'response.data');
  const statusText = get(error, 'response.statusText');

  // Replace verbose AmazonSNS error data from user-facing error toasts
  if (isString(responseData)) return responseData.replace(/\(Service: AmazonSNS.*\)/, '');
  return isString(statusText) ? statusText : FALLBACK_ERROR_MESSAGE;
};

/** Returns whether an integration is legacy firebase based on platform and authType. */
export const getIsLegacyFirebaseIntegration = (
  platform: IntegrationPlatform,
  authType: IntegrationAuthType | undefined,
) => platform === IntegrationPlatform.GCM && authType !== IntegrationAuthType.FIREBASE_JSON;
